import { motion } from 'framer-motion'
import Link from '../components/LocalizedLink'
import { Zap, Scissors, Settings, BadgeCheck, Download, ArrowRight } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'

const BASE = import.meta.env.BASE_URL

const translations = {
  el: {
    hero: {
      title: 'Κοστολόγηση Κοπής Laser Λαμαρίνας',
      subtitle: 'Υπολογίστε το κόστος κοπής laser (ή ψαλιδιού) για κάθε εξάρτημα ελάσματος αυτόματα, βάσει περιμέτρου, υλικού και πάχους.',
      cta1: 'Δωρεάν Δοκιμή',
      cta2: 'Δείτε τις Λειτουργίες',
    },
    intro: {
      title: 'Πώς υπολογίζεται το κόστος laser',
      body: 'Το Workshop Assistant εξάγει αυτόματα την περίμετρο κοπής από τη γεωμετρία DXF ή STEP κάθε εξαρτήματος και υπολογίζει το κόστος κοπής laser βάσει της ταχύτητας κοπής, του κόστους μηχανήματος και του υλικού που έχετε ρυθμίσει. Για απλά περιγράμματα μπορείτε επίσης να ενεργοποιήσετε κοπή με ψαλίδι ως εναλλακτική μέθοδο κοπής.',
    },
    features: {
      title: 'Λειτουργίες κοστολόγησης laser',
      items: [
        { icon: 'zap', title: 'Υπολογισμός Περιμέτρου Κοπής', description: 'Εξάγει αυτόματα το μήκος κοπής από τη γεωμετρία εισαγόμενου αρχείου, χωρίς χειροκίνητη μέτρηση.' },
        { icon: 'scissors', title: 'Laser & Ψαλίδι', description: 'Υποστηρίζει τόσο κοπή laser όσο και ψαλιδιού, με ξεχωριστές ρυθμίσεις κόστους για κάθε μέθοδο.' },
        { icon: 'settings', title: 'Ρυθμίσεις Μηχανήματος', description: 'Προσαρμόστε ταχύτητα κοπής, κόστος ρεύματος και εργατικά ανά υλικό και πάχος.' },
        { icon: 'badge', title: 'Ετικέτα "Laser Cut"', description: 'Κάθε εξάρτημα κοπής laser επισημαίνεται αυτόματα στη λεπτομέρεια εξαρτήματος και στη λίστα προσφορών.' },
      ],
    },
    faq: [
      { q: 'Πώς υπολογίζει το κόστος κοπής laser το Workshop Assistant;', a: 'Βάσει της περιμέτρου κοπής (εξαγόμενης αυτόματα από το DXF/STEP), της ταχύτητας κοπής και του κόστους μηχανήματος/ρεύματος που ορίζετε στον Επεξεργαστή Παραμέτρων.' },
      { q: 'Υποστηρίζει και κοπή με ψαλίδι εκτός από laser;', a: 'Ναι, μπορείτε να ρυθμίσετε κόστος τόσο για laser όσο και για ψαλίδι και να επιλέξετε τη μέθοδο ανά εξάρτημα ή προσφορά.' },
      { q: 'Χρειάζεται να μετρήσω χειροκίνητα την περίμετρο κοπής;', a: 'Όχι, η περίμετρος εξάγεται αυτόματα από τη γεωμετρία του εισαγόμενου αρχείου.' },
    ],
    cta: { title: 'Δοκιμάστε την Κοστολόγηση Laser', subtitle: 'Κατεβάστε τη δωρεάν δοκιμή και κοστολογήστε εξαρτήματα κοπής laser σε δευτερόλεπτα.', btn: 'Δωρεάν Δοκιμή' },
  },
  en: {
    hero: {
      title: 'Laser Cutting Costing for Sheet Metal',
      subtitle: 'Automatically calculate laser (or guillotine) cutting cost for every sheet metal part, based on cut perimeter, material, and thickness.',
      cta1: 'Download Free Trial',
      cta2: 'Explore Features',
    },
    intro: {
      title: 'How laser costing is calculated',
      body: 'Workshop Assistant automatically extracts the cut perimeter from each part\'s DXF or STEP geometry and calculates laser cutting cost based on cutting speed, machine cost, and the material you\'ve configured. For simple contours you can also enable guillotine cutting as an alternative cutting method.',
    },
    features: {
      title: 'Laser costing features',
      items: [
        { icon: 'zap', title: 'Automatic Cut Perimeter', description: 'Extracts cut length directly from the imported file\'s geometry — no manual measuring.' },
        { icon: 'scissors', title: 'Laser & Guillotine', description: 'Supports both laser and guillotine cutting, with separate cost settings for each method.' },
        { icon: 'settings', title: 'Machine Settings', description: 'Configure cutting speed, electricity cost, and labor per material and thickness.' },
        { icon: 'badge', title: '"Laser Cut" Badge', description: 'Every laser-cut part is automatically flagged in the part detail view and offers list.' },
      ],
    },
    faq: [
      { q: 'How does Workshop Assistant calculate laser cutting cost?', a: 'From the cut perimeter (auto-extracted from DXF/STEP), cutting speed, and the machine/electricity cost you set in the Parameters Editor.' },
      { q: 'Does it support guillotine cutting as well as laser?', a: 'Yes, you can configure cost for both laser and guillotine and choose the method per part or offer.' },
      { q: 'Do I need to manually measure the cut perimeter?', a: 'No, the perimeter is extracted automatically from the imported file\'s geometry.' },
    ],
    cta: { title: 'Try Laser Costing Yourself', subtitle: 'Download the free trial and cost laser-cut parts in seconds.', btn: 'Download Free Trial' },
  },
}

const icons = { zap: <Zap className="h-10 w-10" />, scissors: <Scissors className="h-10 w-10" />, settings: <Settings className="h-10 w-10" />, badge: <BadgeCheck className="h-10 w-10" /> }

const LaserCosting = () => {
  const { lang } = useLanguage()
  const t = translations[lang]

  return (
    <div className="overflow-hidden">
      <SEO
        title="Laser Cutting Cost Calculator for Sheet Metal"
        description="Automatically calculate laser and guillotine cutting costs for sheet metal parts based on perimeter, material, and thickness with Workshop Assistant."
        path="/laser-costing"
        faq={t.faq}
      />

      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-6xl font-bold mb-6">{t.hero.title}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-xl opacity-90 mb-8">{t.hero.subtitle}</motion.p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/download" className="flex items-center justify-center space-x-2 px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg">
                <Download className="h-5 w-5" /><span>{t.hero.cta1}</span>
              </Link>
              <Link to="/features" className="flex items-center justify-center space-x-2 px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all duration-200">
                <span>{t.hero.cta2}</span><ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-4">{t.intro.title}</h2>
            <p className="text-lg text-gray-600">{t.intro.body}</p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
            <img src={`${BASE}screenshots/part-detail.png?v=2`} alt={t.hero.title} className="w-full h-auto object-cover" />
          </div>
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">{t.features.title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.features.items.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="card">
              <div className="text-primary-600 mb-4">{icons[f.icon]}</div>
              <h3 className="text-xl font-semibold mb-2">{f.title}</h3>
              <p className="text-gray-600">{f.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">FAQ</h2>
          <div className="space-y-4">
            {t.faq.map((item, i) => (
              <details key={i} className="card group">
                <summary className="font-semibold cursor-pointer list-none flex justify-between items-center">
                  {item.q}
                  <span className="text-primary-600 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-gray-600 mt-3">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section-container bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">{t.cta.title}</h2>
          <p className="text-xl mb-8 opacity-90">{t.cta.subtitle}</p>
          <Link to="/download" className="inline-flex items-center space-x-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200 shadow-lg">
            <Download className="h-5 w-5" /><span>{t.cta.btn}</span>
          </Link>
        </div>
      </section>
    </div>
  )
}

export default LaserCosting
